<template>
	<div
		ref="scrollbarRef"
		class="scrollbar form-tree__container relative h-screen overscroll-none"
	>
		<nav
			class="nav__content py-3"
			:class="{ 'nav__content--mobile' : isMobile }"
		>
			<CUserPins
				:global-store="globalStore"
				:user-pins="userPinsTree"
				:search-query="searchQuery"
				:hidden-items="hiddenItems"
				:selected-nodes="selectedNodes.default"
				@rename="handleRename"
				@select="$emit('select', $event)"
				@has-matches="hasMatches = true"
			/>
			<CFormTree
				class="mb-4"
				tree-mode="default"
				:global-store="globalStore"
				:tree-data="defaultFormTree"
				:search-query="searchQuery"
				:selected-nodes="selectedNodes.default"
				:hidden-items="hiddenItems"
				:scroll-top-observer="scrollTopObserver"
				@rename="handleRename"
				@select="$emit('select', $event)"
				@scroll-children-into-view="scrollChildrenIntoView"
				@has-matches="hasMatches = true"
				@expand="handleResetIntersectionObserver"
			/>
			<div v-if="showNoFormsMessage" class="px-3 flex flex-col place-items-center form-tree__no-forms">
				<Inline svg="complex-icons/getting-started" class="form-tree__image w-33 fill-nav-4" />
				<strong>You don't have any forms.</strong>
			</div>
			<CFormTree
				class="mt-4"
				tree-mode="archived"
				:global-store="globalStore"
				:tree-data="archivedFormTree"
				:search-query="searchQuery"
				:selected-nodes="selectedNodes.archived"
				:hidden-items="hiddenItems"
				:scroll-top-observer="scrollTopObserver"
				@select="$emit('select', $event)"
				@scroll-children-into-view="scrollChildrenIntoView"
				@has-matches="hasMatches = true"
				@expand="handleResetIntersectionObserver"
				@expand-archived="handleArchivedRootExpand"
			/>
			<div v-if="loadingArchived" class="flex items-center mt-3">
				<Inline
					svg="loading"
					class="spin mx-auto"
				/>
			</div>
			<div v-if="searchQuery && hasMatches === false" class="form-tree__no-results px-4 flex flex-col place-items-center">
				<strong>No results found</strong>
				<Inline svg="complex-icons/nil" class="form-tree__image w-33 mt-1" />
			</div>
			<CFormTreeSkeleton v-else-if="!isGlobalStoreReady" class="form-tree__skeleton px-4" />
		</nav>
	</div>
</template>

<script lang="ts">
	import { computed, defineComponent, inject, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue';
	import type { PropType, DeepReadonly } from 'vue';
	import CUserPins from './UserPins.vue';
	import CFormTree, { TreeNodeType, type TreeMode } from './FormTree.vue';
	import CFormTreeSkeleton from './FormTreeSkeleton.vue';
	import { globalStore, type GlobalStore } from 'src/stores/global-store';
	import { isMobileKey, navStateKey, treeNodesKey } from 'src/utilities/injection-keys';
	import { isElementInViewport } from '@cognitoforms/utils/utilities';
	import { useContextMenuState } from './context-menu/use-context-menu';
	import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import { setupKeyboardNavigation } from 'src/composables/use-keyboard-nav';
	import { RenameNodeEvent, useEventBus } from 'src/views/admin/utils/useEventBus';
	import { useRoute } from 'vue-router/composables';

	export type SelectEvent = { id: string, isArchived: boolean, isLocked?: boolean };
	export type RenameEvent = { id: string, type: TreeNodeType, targetTree: TreeMode, newName: string };
	export type ScrollChildrenIntoViewEvent = { id: string, targetTree: TreeMode, height: number };

	type HiddenItems = 'tasks' | 'unread';
	export type HiddenItemOptions = { [key in HiddenItems]?: boolean };

	export default defineComponent({
		name: 'CAppNav',
		components: {
			CUserPins,
			CFormTree,
			CFormTreeSkeleton
		},
		props: {
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			},
			searchQuery: {
				type: String,
				default: ''
			},
			onExpandOrCollapse: {
				type: Function,
				default: () => {}
			}
		},
		emits: ['select', 'scroll-node-into-view', 'has-scroll', 'scroll'],
		setup(props, { emit }) {
			const treeNodes = inject(treeNodesKey);
			const navState = inject(navStateKey);
			const isMobile = inject(isMobileKey);
			const route = useRoute();

			const hasMatches = ref(false);

			const isGlobalStoreReady = ref(false);
			globalStore.isReady.then(async () => {
				isGlobalStoreReady.value = true;
			});

			const showNoFormsMessage = computed(() => (
				isGlobalStoreReady.value
				&& globalStore.organization.Role !== ClientRole.Guest
				&& Object.keys(globalStore.forms).length - (treeNodes.archivedFormTree.value[0]?.children.length ?? 0) === 0
				&& !props.searchQuery
			));

			const hiddenItems = computed<HiddenItemOptions>(() => {
				return {
					tasks: !props.globalStore.organization?.Plan?.AllowWorkflowTasks,
					unread: props.globalStore.organization?.DisableUnreadEntryTracking
				};
			});

			// Scroll node into view logic
			const scrollNodeIntoView = (
				nodeId: string,
				targetTree: TreeMode,
				behavior: ScrollBehavior = 'smooth',
				block: ScrollLogicalPosition = 'center'
			) => {
				const formId = nodeId.split('-')[0];
				const form = props.globalStore.forms[formId];

				// Use formId as nodeId for single view forms; otherwise use id provided
				nodeId = (form.IsSingleViewForm) ? formId : nodeId;

				requestAnimationFrame(() => {
					const node = document.getElementById(`node-${nodeId}-${targetTree}`);
					if (!node || isElementInViewport(node))
						return;

					node.scrollIntoView({ behavior, block });
				});
			};

			// Selected Nodes State
			const initialSelectedNodes = { default: [], archived: [] };
			const selectedNodes = ref<Record<'default' | 'archived', string[]>>(initialSelectedNodes);

			const updateSelectedNode = (
				form: DeepReadonly<UserFormMeta>,
				viewId: string,
				expandNodes: boolean,
				scrollIntoView: boolean
			) => {
				if (!form && !viewId) {
					selectedNodes.value = structuredClone(initialSelectedNodes);
					return;
				}

				const targetTree = form.IsArchived ? 'archived' : 'default';
				const newSelectedNodes = [ form.Id, viewId, ...(form.FolderId) ? [ form.FolderId ] : [] ];

				// If the nodes are the same, do not update the selected nodes state
				const isMatch = (a: string[], b: string[]) => a.length === b.length && a.every((item, i) => item === b[i]);
				if (isMatch(selectedNodes.value[targetTree], newSelectedNodes))
					return;

				selectedNodes.value = structuredClone(initialSelectedNodes);
				selectedNodes.value[targetTree] = newSelectedNodes;

				if (expandNodes) {
					newSelectedNodes.filter(id => id !== viewId).forEach(id => {
						navState.setNodeExpandState(targetTree, id, true);
					});
				}

				if (scrollIntoView) {
					const nodeId = viewId ?? form.Id;
					scrollNodeIntoView(nodeId, targetTree, 'instant', 'center');
				}
			};

			setupKeyboardNavigation();

			watch(
				() => [props.globalStore.currentView?.Id, props.globalStore.currentForm?.Id, props.globalStore.currentForm?.FolderId],
				() => {
					const form = props.globalStore.currentForm;
					const viewId = props.globalStore.currentView?.Id;

					// Prevent updating selected nodes if the route has the lightboxNavigation flag
					// Clearing the selected node state should still be possible
					if (!!form && route.params.lightboxNavigation)
						return;

					updateSelectedNode(form, viewId, true, true);
				},
				{ immediate: true }
			);

			// Set expansion state for guest users
			onBeforeMount(() => {
				if (props.globalStore.organization.Role !== ClientRole.Guest)
					return;

				const autoExpandFormsThreshold = 10;
				const forms = Object.values(globalStore.forms);
				if (autoExpandFormsThreshold > forms.length)
					navState.setTreeExpansionState('default', forms.map(f => f.Id));
				else
					navState.setTreeExpansionState('default', []);
			});

			// Scroll children into view logic
			const smoothScroll = (element: HTMLElement, scrollAmount: number, duration: number) => {
				let startScrollTop: number;
				let distanceToScroll: number;
				let startTime: number;

				const animateScroll = (currentTime: number) => {
					if (!startTime) {
						startTime = currentTime;
						startScrollTop = element.scrollTop;
						distanceToScroll = scrollAmount;
					}

					const elapsedTime = currentTime - startTime;
					const progress = Math.min(elapsedTime / duration, 1);
					element.scrollTop = startScrollTop + distanceToScroll * progress;

					if (progress < 1) {
						requestAnimationFrame(animateScroll);
					}
				};

				requestAnimationFrame(animateScroll);
			};

			const scrollbarRef = ref<HTMLDivElement>(null);
			const supressScroll = ref(false);
			const scrollChildrenIntoView = (e: ScrollChildrenIntoViewEvent) => {
				if (!supressScroll.value) {
					requestAnimationFrame(() => {
						const formTreeNodeElement = document.getElementById(`node-${e.id}-${e.targetTree}`);
						const nodeRect = formTreeNodeElement.getBoundingClientRect();
						const scrollbarRect = scrollbarRef.value.getBoundingClientRect();

						if (!scrollbarRect)
							return;

						// An unexpanded node has a height of 31px
						let scrollBy = -(scrollbarRect.bottom - nodeRect.top - e.height - 31);

						// Prevent scrolling parent node out of view
						const navHeaderOverflowHeight = scrollbarRect.bottom - scrollbarRect.height;
						scrollBy = Math.min(scrollBy, nodeRect.y - navHeaderOverflowHeight);

						if (scrollBy < 0)
							return;

						smoothScroll(scrollbarRef.value, scrollBy, 300);
					});
				}
			};

			// close context menu on scroll
			let hasScroll = false;
			const contextMenu = useContextMenuState();
			const closeContextMenu = () => {
				if (hasScroll)
					contextMenu.isOpen = false;
			};

			const handleScroll = (event) => {
				// Firefox: don't scroll nav if a context menu dialog is open
				if (contextMenu.dialogOpened) {
					return;
				}

				// Ensure context menu is closed
				closeContextMenu();

				emit('scroll', event);
			};

			// Sticky folders
			const scrollTopObserver = ref<IntersectionObserver>(null);

			const onFolderIntersectsScrollTop = (entries) => {
				entries.forEach(({ target, isIntersecting }) => {
					// Get the folder element inside the target <li>
					const folderElement = target?.firstElementChild;
					// Apply the class only if the target <li> is expanded and the folder has children (children live in the sibling)
					if (folderElement && target.classList.contains('form-tree__node--expanded') && folderElement.nextElementSibling) {
						if (isIntersecting) {
							if (document.querySelector('.sticky.form-tree__node--archive'))
								document.querySelector('.sticky.form-tree__node--archive').classList.add('sticky--old');
							target.classList.add('sticky');
						}
						else {
							target.classList.remove('sticky');
							if (document.querySelector('.sticky.form-tree__node--archive') && document.querySelectorAll('.sticky.form-tree__node').length < 2)
								document.querySelector('.sticky.form-tree__node--archive').classList.remove('sticky--old');
						}
					}
				});
			};

			const setupFolderIntersectionObserver = () => {
				const options = {
					root: scrollbarRef.value,
					rootMargin: '-1px 0px -100% 0px',
					threshold: 0
				};

				scrollTopObserver.value = new IntersectionObserver(
					onFolderIntersectsScrollTop,
					options
				);
			};

			const handleResetIntersectionObserver = (root, forceScroll = false) => {
				// handles case where user tries to expand a folder when it is partly intersected
				const scrollContainerRect = scrollbarRef.value.getBoundingClientRect();
				const targetRect = root.getBoundingClientRect();
				const isIntersecting = (targetRect.top - 1) <= scrollContainerRect.top && targetRect.bottom > scrollContainerRect.top;
				if (isIntersecting || forceScroll) {
					supressScroll.value = true;
					root.scrollIntoView();
					setTimeout(() => {
						onFolderIntersectsScrollTop([{ target: root, isIntersecting }]);
						supressScroll.value = false;
					}, 250);
				}
			};

			// Scrollbar resize observer logic
			const scrollbarResizeObserver = ref<ResizeObserver>(null);
			onMounted(() => {
				scrollbarResizeObserver.value = new ResizeObserver(() => {
					hasScroll = scrollbarRef.value.clientHeight < scrollbarRef.value.scrollHeight;
					emit('has-scroll', hasScroll);
				});

				scrollbarRef.value.addEventListener('scroll', handleScroll);
				scrollbarResizeObserver.value.observe(scrollbarRef.value);

				setupFolderIntersectionObserver();
			});

			onUnmounted(() => {
				scrollbarResizeObserver.value.disconnect();
				scrollbarRef.value?.removeEventListener('scroll', handleScroll);
				scrollTopObserver.value?.disconnect();
			});

			watch(() => props.searchQuery, () => {
				hasMatches.value = false;
			});

			props.onExpandOrCollapse(({ expandCollapseOption }) => {
				const includeArchive = navState.expandedNodes['archived'].includes('archive');
				supressScroll.value = true;

				if (expandCollapseOption === 'Expand') {
					// Get folder elements to add sticky class manually when clicking expand/collapse button
					const folderElements = document.querySelectorAll('.form-tree__node--folder, .form-tree__node--archive');
					const scrollContainerRect = scrollbarRef.value.getBoundingClientRect();
					// Expand all folders
					const folders = treeNodes.defaultFormTree.value.filter(node => node.folderMeta);
					folders.forEach(node => {
						navState.setNodeExpandState('default', node.id, true);
					});
					if (includeArchive) {
						const archivedFolders = treeNodes.archivedFormTree.value[0].children.filter(node => node.folderMeta);
						archivedFolders.forEach(node => {
							navState.setNodeExpandState('archived', node.id, true);
						});
					}
					// Apply sticky class after expansion is done
					setTimeout(() => {
						folderElements.forEach(target => {
							const targetRect = target.getBoundingClientRect();
							const isIntersecting = targetRect.top <= scrollContainerRect.top && targetRect.bottom > scrollContainerRect.top;
							onFolderIntersectsScrollTop([{ target, isIntersecting }]);
						});
					}, 250);
				}
				else {
					// Collapse forms and folders
					const openNodes = Array.from(navState.expandedNodes['default'].values());
					openNodes.forEach(node => {
						navState.setNodeExpandState('default', node, false);
					});
					if (includeArchive) {
						const archivedOpenNodes = Array.from(navState.expandedNodes['archived'].values());
						archivedOpenNodes .forEach(node => {
							if (node !== 'archive') { // don't collapse the archive itself
								navState.setNodeExpandState('archived', node, false);
							}
						});
					}
				}
				setTimeout(()=> { supressScroll.value = false; }, 100); // don't allow scroll until after the expansion is done
			});

			const { on: onRenameNodeEvent } = useEventBus(RenameNodeEvent);
			onRenameNodeEvent(() => {
				// reset the search query
				hasMatches.value = null;
				// we need to reset hasMatches, but we don't want 'No Matches Found' to show right away (flash)
				// this set timeout allows for the matches to update before showing 'No Matches Found'
				setTimeout(() => {
					if (hasMatches.value === null)
						hasMatches.value = false;
				}, 300);
			});

			const handleRename = (event: RenameEvent) => {
				// Return node name to non-editable state
				navState.clearNodeRenameState();

				if (event.targetTree === 'pinned') {
					props.globalStore.renameUserPin(event.id, event.newName);
				}
				else {
					switch (event.type) {
					case TreeNodeType.Folder:
						props.globalStore.renameFolder(event.id, event.newName);
						break;
					case TreeNodeType.View:
						props.globalStore.renameView(event.id, event.newName);
					}
				}
			};

			const loadingArchived = ref(false);

			const handleArchivedRootExpand = async (event) => {
				loadingArchived.value = true;
				await props.globalStore.loadArchivedFormsIfNeeded().then(() => {
					handleResetIntersectionObserver(event, true);
					loadingArchived.value = false;
				});
			};

			return {
				isMobile,
				showNoFormsMessage,
				defaultFormTree: treeNodes.defaultFormTree,
				archivedFormTree: treeNodes.archivedFormTree,
				userPinsTree: treeNodes.userPinsTree,
				hiddenItems,
				selectedNodes,
				scrollNodeIntoView,
				scrollChildrenIntoView,
				scrollbarRef,
				handleRename,
				hasMatches,
				scrollTopObserver,
				handleResetIntersectionObserver,
				handleArchivedRootExpand,
				loadingArchived,
				isGlobalStoreReady
			};
		}
	});
</script>

<style lang="scss">
.form-tree {

	&__image {
		max-width: 126px;

		&.i-complex > svg > [fill] {
			fill: var(--shade-4);
		}
	}

	&__skeleton {
		display: none;
		margin-top: 8px;

		.form-tree--default:empty ~ & {
			display: block;
		}
	}
}

:root:root .nav__content--mobile {
	// Prevent new button from covering bottom forms entry count on mobile
	padding-bottom: 48px;
}

</style>